// src/context/PrivacyContext.js
//
// App-wide blur / privacy mode.
//
// History: this used to be a Dashboard-local context (Dashboard.js) so
// only the Dashboard's $ amounts blurred out. Lifted here so every page
// can opt into the same global toggle and the Settings → Privacy →
// "Blur mode" switch acts as a single source of truth.
//
// Two consumption patterns are supported:
//
//   1. Content masking (preferred for $ amounts) — wrap the value with
//      <Hide>{formatCurrency(x)}</Hide>. When privacy is on, the inner
//      content is replaced with a fixed-length mask (so the blurred
//      shape gives away no magnitude). Hover to briefly reveal.
//
//   2. CSS blur (for charts, lists, anywhere wrapping is awkward) — add
//      `className="privacy-sensitive"` to the element. CSS in App.css
//      blurs it whenever the root has `data-privacy-mode="on"`.
//
// Both modes work together — pages can use whichever fits per spot.
//
// Persistence: localStorage('privacyMode'). Reads backward-compat from
// the old 'dashboard.privacyMode' key on first load so users who had
// blur enabled before this lift keep their setting.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PRIVACY_LS_KEY = 'privacyMode';
const LEGACY_LS_KEY = 'dashboard.privacyMode';

const PrivacyContext = createContext({
  privacyMode: false,
  setPrivacyMode: () => {},
  togglePrivacyMode: () => {},
});

export const PrivacyProvider = ({ children }) => {
  const [privacyMode, setPrivacyMode] = useState(() => {
    try {
      // Prefer the new key. Fall back to the legacy key so existing
      // users don't lose their setting on the lift.
      const fresh = window.localStorage.getItem(PRIVACY_LS_KEY);
      if (fresh !== null) return fresh === 'true';
      const legacy = window.localStorage.getItem(LEGACY_LS_KEY);
      return legacy === 'true';
    } catch {
      return false;
    }
  });

  // Persist + set the ancestor attribute the CSS selector keys off of.
  // Setting it on <html> means EVERY .privacy-sensitive descendant
  // across the whole app blurs, no per-page wiring needed.
  useEffect(() => {
    try { window.localStorage.setItem(PRIVACY_LS_KEY, String(privacyMode)); } catch {}
    document.documentElement.setAttribute('data-privacy-mode', privacyMode ? 'on' : 'off');
  }, [privacyMode]);

  const togglePrivacyMode = useCallback(() => setPrivacyMode((p) => !p), []);

  return (
    <PrivacyContext.Provider value={{ privacyMode, setPrivacyMode, togglePrivacyMode }}>
      {children}
    </PrivacyContext.Provider>
  );
};

/**
 * Hook to read + control privacy mode.
 *   const { privacyMode, setPrivacyMode, togglePrivacyMode } = usePrivacy();
 */
export const usePrivacy = () => useContext(PrivacyContext);

/**
 * <Hide>$1,234.56</Hide> → renders the value normally when privacy is
 * off, swaps it for `mask` (default `$•••••`) when privacy is on.
 * Hovering the masked element briefly reveals the real value — the
 * screenshare risk is gone the moment the cursor moves.
 *
 * Use `mask` to customize the placeholder for non-currency values
 * (e.g. product names: <Hide mask="••••••••">Charizard Holo</Hide>).
 */
export const Hide = ({ children, mask = '$•••••' }) => {
  const { privacyMode } = usePrivacy();
  const [hover, setHover] = useState(false);
  if (!privacyMode) return <>{children}</>;
  return (
    <span
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ cursor: 'help' }}
    >
      {hover ? children : mask}
    </span>
  );
};

export default PrivacyContext;
